/**
 * Utility functions for handling array fields in dynamic tabs
 * Supports expanding tabs based on array markers [] in field names
 */

/**
 * Checks if field name has array marker [] or [main]
 * @param {string} fieldName - Field name to check
 * @returns {boolean}
 */
export const hasArrayMarker = (fieldName) => {
    if (typeof fieldName !== 'string') return false;
    return fieldName.endsWith('[]') || fieldName.endsWith('[main]');
};

/**
 * Checks if field name has main marker [main]
 * @param {string} fieldName - Field name to check
 * @returns {boolean}
 */
export const hasMainMarker = (fieldName) => {
    return typeof fieldName === 'string' && fieldName.endsWith('[main]');
};

/**
 * Extracts hint text from field name {hint text}
 * @param {string} fieldName - Field name to check
 * @returns {string|null} - Hint text or null if no hint
 */
export const extractHintText = (fieldName) => {
    if (typeof fieldName !== 'string') return null;
    const match = fieldName.match(/\{([^}]+)\}/);
    return match ? match[1] : null;
};

/**
 * Removes array marker [] or [main] and {hint} from field name
 * @param {string} fieldName - Field name with possible [] or [main] or {hint}
 * @returns {string}
 */
export const cleanFieldName = (fieldName) => {
    if (typeof fieldName !== 'string') return fieldName;
    return fieldName
        .replace(/\[\]$/, '')
        .replace(/\[main\]$/, '')
        .replace(/\{[^}]+\}/g, '');
};

/**
 * Ensures value is an array (wraps non-arrays)
 * @param {*} value - Value to ensure as array
 * @returns {Array}
 */
const ensureArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined) return [];
    return [value];
};

/**
 * Calculates maximum number of tabs needed based on array fields
 * Priority: [main] marker takes precedence over []
 * @param {Object} tabsConfig - Configuration with field names (some with [], [main])
 * @param {Object} data - Data object with actual values
 * @returns {number} - Maximum tabs needed (minimum 1)
 */
export const calculateMaxTabs = (tabsConfig, data) => {
    if (!tabsConfig || !data) return 1;

    let mainFieldLength = null;
    let maxLength = 0;

    // First pass: Check if there's a [main] field
    let hasMainField = false;
    Object.values(tabsConfig).forEach(fieldNames => {
        if (!Array.isArray(fieldNames)) return;

        fieldNames.forEach(fieldName => {
            if (hasMainMarker(fieldName)) {
                hasMainField = true;
                const cleanName = cleanFieldName(fieldName);
                const value = data[cleanName];

                // If [main] field is an array, use its length
                // If [main] field is NOT an array, use 1 (single tab)
                if (Array.isArray(value)) {
                    mainFieldLength = value.length;
                } else {
                    mainFieldLength = 1;
                }
            }
        });
    });

    // If [main] field found, return its length (array) or 1 (single value)
    if (hasMainField) {
        return Math.max(1, mainFieldLength);
    }

    // No [main] field - calculate based on all [] fields
    Object.values(tabsConfig).forEach(fieldNames => {
        if (!Array.isArray(fieldNames)) return;

        // Check each field in the tab
        fieldNames.forEach(fieldName => {
            if (hasArrayMarker(fieldName)) {
                const cleanName = cleanFieldName(fieldName);
                const value = data[cleanName];

                // Only count REAL arrays, not wrapped non-arrays
                if (Array.isArray(value)) {
                    maxLength = Math.max(maxLength, value.length);
                }
                // If value is not an array, ignore it for tab count calculation
            }
        });
    });

    // Return at least 1 tab
    return Math.max(1, maxLength);
};

/**
 * Calculates maximum tabs needed for a specific tab based on its fields
 * @param {Array} fieldNames - Field names for the tab
 * @param {Object} data - Data object with actual values
 * @returns {number} - Maximum tabs needed for this specific tab
 */
const calculateMaxTabsForTab = (fieldNames, data) => {
    if (!Array.isArray(fieldNames) || !data) return 1;

    let mainFieldLength = null;
    let maxLength = 0;

    // First pass: Check if there's a [main] field in THIS tab
    let hasMainField = false;
    fieldNames.forEach(fieldName => {
        if (hasMainMarker(fieldName)) {
            hasMainField = true;
            const cleanName = cleanFieldName(fieldName);
            const value = data[cleanName];

            // If [main] field is an array, use its length
            // If [main] field is NOT an array, use 1 (single tab)
            if (Array.isArray(value)) {
                mainFieldLength = value.length;
            } else {
                mainFieldLength = 1;
            }
        }
    });

    // If [main] field found in THIS tab, return its length
    if (hasMainField) {
        return Math.max(1, mainFieldLength);
    }

    // No [main] field - calculate based on [] fields in THIS tab
    fieldNames.forEach(fieldName => {
        if (hasArrayMarker(fieldName)) {
            const cleanName = cleanFieldName(fieldName);
            const value = data[cleanName];

            // Only count REAL arrays, not wrapped non-arrays
            if (Array.isArray(value)) {
                maxLength = Math.max(maxLength, value.length);
            }
        }
    });

    // Return at least 1 tab
    return Math.max(1, maxLength);
};

/**
 * Expands tabs configuration to create multiple tabs for array fields
 * @param {Object} tabsConfig - Original tabs configuration
 * @param {Object} data - Data object with actual values
 * @returns {Object} - Expanded tabs configuration
 */
export const expandTabs = (tabsConfig, data) => {
    if (!tabsConfig || !data) return tabsConfig;

    const expandedTabs = {};

    Object.entries(tabsConfig).forEach(([tabName, fieldNames]) => {
        // Check if this tab has any array fields
        const tabHasArrayFields = Array.isArray(fieldNames) && fieldNames.some(hasArrayMarker);

        if (!tabHasArrayFields) {
            // No array fields in this tab, keep as is
            expandedTabs[tabName] = fieldNames;
        } else {
            // Calculate max tabs needed for THIS specific tab only
            const maxTabsForThisTab = calculateMaxTabsForTab(fieldNames, data);

            // Expand this tab into multiple tabs based on ITS OWN fields
            for (let i = 0; i < maxTabsForThisTab; i++) {
                const expandedTabName = `${tabName} ${i + 1}`;
                expandedTabs[expandedTabName] = fieldNames;
            }
        }
    });

    return expandedTabs;
};

/**
 * Distributes data across multiple tabs based on array markers
 * @param {Object} data - Original data object
 * @param {Object} originalTabsConfig - Original tabs configuration (before expansion)
 * @param {number} tabIndex - Index of the tab (0-based)
 * @param {string} currentTabName - Name of the current tab (optional, for optimization)
 * @returns {Object} - Data for specific tab
 */
export const distributeDataToTab = (data, originalTabsConfig, tabIndex, currentTabName = null) => {
    if (!data || !originalTabsConfig) return data;

    const distributedData = {};

    // Get all field names from all tabs
    const allFields = new Set();
    Object.values(originalTabsConfig).forEach(fieldNames => {
        if (Array.isArray(fieldNames)) {
            fieldNames.forEach(fieldName => {
                allFields.add(cleanFieldName(fieldName));
            });
        }
    });

    // Create a map of field names to their tab's field list (for context-aware checking)
    const fieldToTabFields = {};
    Object.entries(originalTabsConfig).forEach(([tabName, fieldNames]) => {
        if (Array.isArray(fieldNames)) {
            fieldNames.forEach(fieldName => {
                const cleanName = cleanFieldName(fieldName);
                if (!fieldToTabFields[cleanName]) {
                    fieldToTabFields[cleanName] = [];
                }
                fieldToTabFields[cleanName].push({ tabName, fieldNames, originalFieldName: fieldName });
            });
        }
    });

    // Distribute data for each field
    allFields.forEach(cleanName => {
        const value = data[cleanName];

        // Check if THIS specific field has array marker in ITS OWN tab
        let hasMarker = false;
        const fieldOccurrences = fieldToTabFields[cleanName] || [];

        for (const occurrence of fieldOccurrences) {
            // Check if this field has array marker in its original form
            if (hasArrayMarker(occurrence.originalFieldName)) {
                hasMarker = true;
                break;
            }
        }

        if (hasMarker) {
            // Field has [] or [main] marker somewhere
            if (Array.isArray(value)) {
                // Value is a real array - extract element by index
                distributedData[cleanName] = value[tabIndex] !== undefined ? value[tabIndex] : null;
            } else {
                // Value is NOT an array - duplicate the constant value across all tabs
                distributedData[cleanName] = value;
            }
        } else {
            // Field without [] - duplicate as is
            distributedData[cleanName] = value;
        }
    });

    return distributedData;
};

/**
 * Collects data from multiple tabs back into arrays
 * @param {Object} expandedTabs - Expanded tabs configuration
 * @param {Object} originalTabsConfig - Original tabs configuration
 * @param {Function} getTabData - Function to get data for specific tab index (tabIndex) => data
 * @returns {Object} - Collected data with arrays
 */
export const collectDataFromTabs = (expandedTabs, originalTabsConfig, getTabData) => {
    if (!expandedTabs || !originalTabsConfig || !getTabData) return {};

    const collectedData = {};

    // Find which tabs were expanded and map them to their original configs
    const expandedTabGroups = {};
    Object.keys(expandedTabs).forEach(expandedTabName => {
        // Check if this is an expanded tab (ends with " N")
        const match = expandedTabName.match(/^(.+)\s+(\d+)$/);
        if (match) {
            const originalName = match[1];
            const index = parseInt(match[2], 10) - 1;

            if (!expandedTabGroups[originalName]) {
                expandedTabGroups[originalName] = {
                    indices: [],
                    fieldNames: originalTabsConfig[originalName] || []
                };
            }
            expandedTabGroups[originalName].indices.push(index);
        }
    });

    // Collect data only from tabs that were expanded (have array markers)
    Object.entries(expandedTabGroups).forEach(([tabName, { indices, fieldNames }]) => {
        if (!Array.isArray(fieldNames)) return;

        // Process each field in this specific tab
        fieldNames.forEach(fieldName => {
            const cleanName = cleanFieldName(fieldName);
            const hasMarker = hasArrayMarker(fieldName);

            if (hasMarker) {
                // This field has [] or [main] marker
                // Only collect as array if multiple tabs were created (real split occurred)
                const arrayData = [];

                for (let i = 0; i < indices.length; i++) {
                    const tabData = getTabData(i);
                    arrayData.push(tabData && tabData[cleanName] !== undefined ? tabData[cleanName] : null);
                }

                // If only 1 tab exists, return single value (no array wrapping)
                // If multiple tabs exist, return array (real split occurred)
                collectedData[cleanName] = indices.length > 1 ? arrayData : arrayData[0];
            } else {
                // This field doesn't have [] marker but is in an expanded tab
                // If multiple tabs exist, collect as array
                // If single tab, return single value
                const values = [];

                for (let i = 0; i < indices.length; i++) {
                    const tabData = getTabData(i);
                    values.push(tabData && tabData[cleanName] !== undefined ? tabData[cleanName] : null);
                }

                // If only 1 tab exists, return single value (no array wrapping)
                // If multiple tabs exist, return array (real split occurred)
                collectedData[cleanName] = indices.length > 1 ? values : values[0];
            }
        });
    });

    // Also collect data from non-expanded tabs (tabs without array markers)
    Object.entries(originalTabsConfig).forEach(([tabName, fieldNames]) => {
        // Skip if this tab was expanded
        if (expandedTabGroups[tabName]) return;

        if (!Array.isArray(fieldNames)) return;

        // This tab was not expanded - collect single values from first tab (index 0)
        fieldNames.forEach(fieldName => {
            const cleanName = cleanFieldName(fieldName);

            // Skip if already collected
            if (collectedData[cleanName] !== undefined) return;

            const tabData = getTabData(0);
            collectedData[cleanName] = tabData && tabData[cleanName] !== undefined ? tabData[cleanName] : null;
        });
    });

    return collectedData;
};

/**
 * Gets the original tab name from expanded tab name
 * @param {string} expandedTabName - Expanded tab name (e.g., "Customer(s) 2")
 * @returns {string} - Original tab name (e.g., "Customer(s)")
 */
export const getOriginalTabName = (expandedTabName) => {
    const match = expandedTabName.match(/^(.+)\s+\d+$/);
    return match ? match[1] : expandedTabName;
};

/**
 * Gets tab index from expanded tab name
 * @param {string} expandedTabName - Expanded tab name (e.g., "Customer(s) 2")
 * @returns {number} - Tab index (0-based)
 */
export const getTabIndex = (expandedTabName) => {
    const match = expandedTabName.match(/^(.+)\s+(\d+)$/);
    return match ? parseInt(match[2], 10) - 1 : 0;
};

/**
 * Checks if tabs configuration has any array markers
 * @param {Object} tabsConfig - Tabs configuration
 * @returns {boolean} - True if any tab has array markers
 */
export const hasAnyArrayMarkers = (tabsConfig) => {
    if (!tabsConfig) return false;

    return Object.values(tabsConfig).some(fieldNames =>
        Array.isArray(fieldNames) && fieldNames.some(hasArrayMarker)
    );
};
